import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { RewardV1SearchResult } from '../types/rewardSearchV1';

type LocationState = {
  rewardOption?: RewardV1SearchResult;
  currentAmount?: number;
  currentFrequency?: string;
  from?: string;
};

const rpValueInInr = 0.25;

const getMonthlyAmount = (amount: number, frequency?: string) => {
  switch (frequency) {
    case 'quarterly':
      return amount / 3;
    case 'half_yearly':
      return amount / 6;
    case 'annually':
      return amount / 12;
    default:
      return amount;
  }
};

const RewardOptionDetailV1: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;

  const rewardOption = state.rewardOption;
  const currentAmount = state.currentAmount;
  const currentFrequency = state.currentFrequency;

  useEffect(() => {
    document.title = 'Reward Option Detail';
  }, []);

  const monthlyEquivalent = useMemo(() => {
    if (typeof currentAmount !== 'number' || !Number.isFinite(currentAmount)) return null;
    return getMonthlyAmount(currentAmount, currentFrequency);
  }, [currentAmount, currentFrequency]);

  const effectiveRate = rewardOption?.rewards?.dynamic?.effective_rate_per_inr;

  const calculatedRewardPoints = useMemo(() => {
    if (!rewardOption) return null;
    if (typeof currentAmount !== 'number' || !Number.isFinite(currentAmount) || currentAmount <= 0) return null;
    if (typeof effectiveRate !== 'number') return null;

    const monthlyAmount = getMonthlyAmount(currentAmount, currentFrequency);
    let points = monthlyAmount * effectiveRate;

    const monthlyCap = rewardOption.rewards?.limit;
    if (typeof monthlyCap === 'number' && monthlyCap > 0 && points > monthlyCap) {
      points = monthlyCap;
    }

    return Math.round(points);
  }, [rewardOption, currentAmount, currentFrequency, effectiveRate]);

  const calculatedPercentage = useMemo(() => {
    if (calculatedRewardPoints === null) return null;
    if (monthlyEquivalent === null || monthlyEquivalent <= 0) return null;
    const rewardValue = calculatedRewardPoints * rpValueInInr;
    return Math.round((rewardValue / monthlyEquivalent) * 100 * 10) / 10;
  }, [calculatedRewardPoints, monthlyEquivalent]);

  if (!rewardOption) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Reward Option Detail</h1>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </button>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-700">No reward option provided. Open this page from a reward search result.</div>
        </div>
      </div>
    );
  }

  const r = rewardOption.rewards;
  const d = rewardOption.rewards?.dynamic;
  const caps = rewardOption.rewards?.caps;
  const earnBreakdown = rewardOption.rewards?.earn_breakdown || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reward Option Detail</h1>
          <p className="mt-1 text-sm text-gray-500">{rewardOption.name}</p>
        </div>
        <button
          onClick={() => {
            if (state.from) {
              navigate(state.from);
            } else {
              navigate(-1);
            }
          }}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-lg font-semibold text-gray-900">{rewardOption.name}</div>
        <div className="mt-1 text-sm text-gray-600">
          {(rewardOption.bank_name || '—')} — {(rewardOption.card_name || '—')}
        </div>
      </div>

      {typeof currentAmount === 'number' && calculatedRewardPoints !== null && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-sm font-semibold text-gray-900">Your Reward Summary</h2>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Expense Amount</div>
              <div className="mt-1 text-sm font-medium text-gray-900">
                ₹{monthlyEquivalent?.toLocaleString('en-IN')} {currentFrequency ? `(${currentFrequency})` : ''}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">You Earn</div>
              <div className="mt-1 text-sm font-medium text-gray-900">
                {calculatedRewardPoints.toLocaleString('en-IN')} Reward Points
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Reward Rate</div>
              <div className="mt-1 text-sm font-medium text-gray-900">{calculatedPercentage ?? '—'}%</div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-sm font-semibold text-gray-900">Reward Details</h2>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Description</div>
            <div className="mt-1 text-sm text-gray-800">{r?.description || '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Reward Value</div>
            <div className="mt-1 text-sm text-gray-800">{typeof r?.value === 'number' ? `${r.value} ${r.unit || ''}` : '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Minimum Spend</div>
            <div className="mt-1 text-sm text-gray-800">{r?.min_spend ? `₹${r.min_spend}` : '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Maximum Spend</div>
            <div className="mt-1 text-sm text-gray-800">{r?.max_spend ? `₹${r.max_spend}` : '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Monthly Cap</div>
            <div className="mt-1 text-sm text-gray-800">{typeof r?.limit === 'number' ? `${r.limit.toLocaleString('en-IN')} RPs` : '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Frequency</div>
            <div className="mt-1 text-sm text-gray-800">{r?.frequency || '—'}</div>
          </div>
          <div className="sm:col-span-2">
            <div className="text-xs uppercase tracking-wider text-gray-500">Exclusions</div>
            <div className="mt-1 text-sm text-gray-800">{r?.exclusions || '—'}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-sm font-semibold text-gray-900">Dynamic Rewards</h2>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Base Value</div>
            <div className="mt-1 text-sm text-gray-800">
              {typeof d?.base_value === 'number' && typeof d?.base_spend_unit === 'number'
                ? `${d.base_value} RPs per ₹${d.base_spend_unit}`
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Accelerated Value</div>
            <div className="mt-1 text-sm text-gray-800">
              {typeof d?.accelerated_value === 'number' && typeof d?.accelerated_spend_unit === 'number'
                ? `${d.accelerated_value} RPs per ₹${d.accelerated_spend_unit}`
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Acceleration Factor</div>
            <div className="mt-1 text-sm text-gray-800">{typeof d?.acceleration_factor === 'number' ? `${d.acceleration_factor}x` : '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Effective Rate</div>
            <div className="mt-1 text-sm text-gray-800">
              {typeof d?.effective_rate_per_inr === 'number' ? `${d.effective_rate_per_inr.toFixed(4)} RPs per ₹1` : '—'}
            </div>
          </div>
          <div className="sm:col-span-2">
            <div className="text-xs uppercase tracking-wider text-gray-500">Formula</div>
            <div className="mt-1 text-sm text-gray-800">{d?.formula || '—'}</div>
          </div>
          <div className="sm:col-span-2">
            <div className="text-xs uppercase tracking-wider text-gray-500">Notes</div>
            <div className="mt-1 text-sm text-gray-800">{d?.notes || '—'}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-sm font-semibold text-gray-900">Earn Breakdown</h2>
        <div className="mt-3 space-y-2">
          {earnBreakdown.map((b, idx) => (
            <div key={`${b.component || 'c'}-${idx}`} className="border border-gray-200 rounded-md p-3">
              <div className="text-sm font-medium text-gray-900">{b.component || '—'}</div>
              <div className="mt-1 text-sm text-gray-800">Rate: {typeof b.rate_rps_per_inr === 'number' ? `${b.rate_rps_per_inr.toFixed(4)} RPs per ₹1` : '—'}</div>
              <div className="mt-1 text-sm text-gray-700">{b.description || '—'}</div>
            </div>
          ))}
          {earnBreakdown.length === 0 && <div className="text-sm text-gray-500">No breakdown available.</div>}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-sm font-semibold text-gray-900">Caps & Limits</h2>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Monthly Reward Points Cap</div>
            <div className="mt-1 text-sm text-gray-800">
              {typeof caps?.monthly_reward_points === 'number' ? `${caps.monthly_reward_points.toLocaleString('en-IN')} RPs` : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Monthly Portal Spend Cap</div>
            <div className="mt-1 text-sm text-gray-800">
              {typeof caps?.monthly_portal_spend_cap_inr === 'number'
                ? `₹${caps.monthly_portal_spend_cap_inr.toLocaleString('en-IN')}`
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Monthly Voucher Spend Cap</div>
            <div className="mt-1 text-sm text-gray-800">
              {typeof caps?.monthly_voucher_spend_cap_inr === 'number'
                ? `₹${caps.monthly_voucher_spend_cap_inr.toLocaleString('en-IN')}`
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Voucher Cap Description</div>
            <div className="mt-1 text-sm text-gray-800">{caps?.voucher_spend_cap_description || '—'}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-sm font-semibold text-gray-900">Conditions</h2>
        <div className="mt-3">
          {(r?.conditions || []).length > 0 ? (
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-800">
              {(r?.conditions || []).map((c, idx) => (
                <li key={`${idx}-${c.slice(0, 20)}`}>{c}</li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-500">No conditions listed.</div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-sm font-semibold text-gray-900">Category</h2>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Primary Category</div>
            <div className="mt-1 text-sm text-gray-800">{rewardOption.categoryprimary || '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Secondary Categories</div>
            <div className="mt-1 text-sm text-gray-800">{rewardOption.categorysecondary?.join(', ') || '—'}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-sm font-semibold text-gray-900">Additional Information</h2>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Reward Type</div>
            <div className="mt-1 text-sm text-gray-800">{rewardOption.rewardtype || '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Direct</div>
            <div className="mt-1 text-sm text-gray-800">{rewardOption.direct || '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Earn Rewards</div>
            <div className="mt-1 text-sm text-gray-800">{rewardOption.earnrewards || '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Status</div>
            <div className="mt-1">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${rewardOption.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
              >
                {rewardOption.status || '—'}
              </span>
            </div>
          </div>
          <div className="sm:col-span-2">
            <div className="text-xs uppercase tracking-wider text-gray-500">Full Description</div>
            <div className="mt-1 text-sm text-gray-800">{rewardOption.description || '—'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardOptionDetailV1;
